import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../api';
import ExamList from './ExamList';
import ExamModal from './ExamModal';
import GroupManager from './GroupManager';
import GroupModal from './GroupModal';

export default function ExamsSection({ token, setError }) {
  const [orgs, setOrgs] = useState(null);
  const [types, setTypes] = useState(null);
  const [cycles, setCycles] = useState(null);
  const [tests, setTests] = useState(null);

  const [level, setLevel] = useState('org');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);

  const [subjectGroups, setSubjectGroups] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [showGroupsManager, setShowGroupsManager] = useState(false);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('add');
  const [groupModalData, setGroupModalData] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalData, setModalData] = useState(null);

  const location = useLocation();
  const [lastAppliedTestId, setLastAppliedTestId] = useState(null);

  const fetchOrgs = async () => {
    try {
      const res = await api.get('/admin/organizations', { headers: { Authorization: `Bearer ${token}` } });
      setOrgs(res.data.organizations);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organizations');
    }
  };

  const fetchTypes = async (orgId) => {
    try {
      const res = await api.get('/admin/exam-types', { headers: { Authorization: `Bearer ${token}` } });
      const filtered = res.data.exam_types.filter((t) => t.organization_id === orgId);
      setTypes(filtered);
      return filtered;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch exam types');
      return [];
    }
  };

  const fetchCycles = async (typeId) => {
    try {
      const res = await api.get('/admin/exam-cycles', { headers: { Authorization: `Bearer ${token}` } });
      const filtered = res.data.exam_cycles.filter((c) => c.exam_type_id === typeId);
      setCycles(filtered);
      return filtered;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch exam cycles');
      return [];
    }
  };

  const fetchTests = async (cycleId) => {
    try {
      const res = await api.get('/admin/tests', { headers: { Authorization: `Bearer ${token}` } });
      const filtered = res.data.tests.filter((t) => t.exam_cycle_id === cycleId);
      setTests(filtered);
      return filtered;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tests');
      return [];
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await api.get('/admin/subjects', { headers: { Authorization: `Bearer ${token}` } });
      setSubjects(res.data.subjects);
      return res.data.subjects;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch subjects');
      return [];
    }
  };

  const fetchSubjectGroups = async (testId) => {
    try {
      const res = await api.get(`/admin/tests/${testId}/subject-groups`, { headers: { Authorization: `Bearer ${token}` } });
      setSubjectGroups(res.data.groups);
      return res.data.groups;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch subject groups');
      return [];
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchOrgs();
    fetchSubjects();
  }, [token]);

  useEffect(() => {
    const state = location.state || {};
    const { orgId, typeId, cycleId, testId } = state;
    if (!orgs) return;
    if (!typeId || !cycleId || !testId) return;
    if (testId === lastAppliedTestId) return;

    const applySelection = async () => {
      const org = orgs.find((o) => o.id === orgId) || orgs[0];
      if (!org) return;
      setSelectedOrg(org);
      setLevel('type');

      const typesResolved = await fetchTypes(org.id);
      const type = typesResolved.find((t) => t.id === typeId) || typesResolved[0];
      if (!type) return;
      setSelectedType(type);
      setLevel('cycle');

      const cyclesResolved = await fetchCycles(type.id);
      const cycle = cyclesResolved.find((c) => c.id === cycleId) || cyclesResolved[0];
      if (!cycle) return;
      setSelectedCycle(cycle);
      setLevel('test');

      const testsResolved = await fetchTests(cycle.id);
      const test = testsResolved.find((t) => t.id === testId) || testsResolved[0];
      if (test) {
        setSelectedTest(test);
        setShowGroupsManager(true);
        fetchSubjectGroups(test.id);
      }
      setLastAppliedTestId(testId);
    };

    applySelection();
  }, [orgs, location.state, lastAppliedTestId]);

  const saveItem = async () => {
    try {
      let url = '';
      let payload = null;
      let method = 'post';

      const statusValue = ['active', 'draft', 'suspended'].includes(modalData?.status) ? modalData.status : 'active';
      if (level === 'org') {
        url = '/admin/organizations';
        payload = { ...modalData, status: statusValue };
      } else if (level === 'type') {
        url = '/admin/exam-types';
        payload = { ...modalData, status: statusValue };
      } else if (level === 'cycle') {
        url = '/admin/exam-cycles';
        payload = { ...modalData, status: statusValue };
      } else {
        // test
        url = '/admin/tests';
        const qCount = Number(modalData.total_questions || 0);
        const marksCorrect = Number(modalData.marks_per_correct || 1);
        payload = { ...modalData, total_marks: qCount * marksCorrect, status: statusValue };
      }

      if (modalMode === 'edit') {
        method = 'put';
        url += `/${modalData.id}`;
      }

      await api[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
      setModalOpen(false);

      if (level === 'org') fetchOrgs();
      if (level === 'type' && selectedOrg) fetchTypes(selectedOrg.id);
      if (level === 'cycle' && selectedType) fetchCycles(selectedType.id);
      if (level === 'test' && selectedCycle) fetchTests(selectedCycle.id);
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  const deleteItem = async (id) => {
    try {
      let url = '';
      if (level === 'org') url = `/admin/organizations/${id}`;
      if (level === 'type') url = `/admin/exam-types/${id}`;
      if (level === 'cycle') url = `/admin/exam-cycles/${id}`;
      if (level === 'test') url = `/admin/tests/${id}`;

      await api.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      if (level === 'org') fetchOrgs();
      if (level === 'type' && selectedOrg) fetchTypes(selectedOrg.id);
      if (level === 'cycle' && selectedType) fetchCycles(selectedType.id);
      if (level === 'test' && selectedCycle) fetchTests(selectedCycle.id);
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  const updateStatus = async (item, status) => {
    if (!['active', 'draft', 'suspended'].includes(status)) return;

    try {
      let url = '';
      if (level === 'org') url = `/admin/organizations/${item.id}/status`;
      if (level === 'type') url = `/admin/exam-types/${item.id}/status`;
      if (level === 'cycle') url = `/admin/exam-cycles/${item.id}/status`;
      if (level === 'test') url = `/admin/tests/${item.id}/status`;

      await api.put(url, { status }, { headers: { Authorization: `Bearer ${token}` } });
      if (level === 'test') {
        setTests((prev) => (prev || []).map((t) => (t.id === item.id ? { ...t, status } : t)));
      }
    } catch (e) {
      // Debug and fallback: older backend may not support /status route yet
      console.error('Status update failed, falling back to full update:', e.response?.data || e.message);
      if (level === 'test') {
        try {
          await api.put(`/admin/tests/${item.id}`, {
            exam_cycle_id: item.exam_cycle_id,
            title: item.title,
            duration_minutes: item.duration_minutes,
            total_questions: item.total_questions,
            marks_per_correct: item.marks_per_correct,
            negative_mark: item.negative_mark,
            total_marks: item.total_marks,
            status,
          }, { headers: { Authorization: `Bearer ${token}` } });
          setTests((prev) => (prev || []).map((t) => (t.id === item.id ? { ...t, status } : t)));
        } catch (e2) {
          setError(e2.response?.data?.error || 'Failed to update status via fallback');
          return;
        }
      } else if (level === 'org') {
        try {
          await api.put(`/admin/organizations/${item.id}`, { ...item, status }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e2) {
          setError(e2.response?.data?.error || 'Failed to update status via fallback');
          return;
        }
      } else if (level === 'type') {
        try {
          await api.put(`/admin/exam-types/${item.id}`, { ...item, status }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e2) {
          setError(e2.response?.data?.error || 'Failed to update status via fallback');
          return;
        }
      } else if (level === 'cycle') {
        try {
          await api.put(`/admin/exam-cycles/${item.id}`, { ...item, status }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e2) {
          setError(e2.response?.data?.error || 'Failed to update status via fallback');
          return;
        }
      }
    }

    if (level === 'org') fetchOrgs();
    if (level === 'type' && selectedOrg) fetchTypes(selectedOrg.id);
    if (level === 'cycle' && selectedType) fetchCycles(selectedType.id);
    if (level === 'test' && selectedCycle) fetchTests(selectedCycle.id);
  };

  const openModal = (mode, data = null) => {
    setModalMode(mode);
    if (mode === 'add') {
      if (level === 'type' && selectedOrg) data = { organization_id: selectedOrg.id };
      if (level === 'cycle' && selectedType) data = { exam_type_id: selectedType.id };
      if (level === 'test' && selectedCycle) data = { exam_cycle_id: selectedCycle.id };
    }
    setModalData(data);
    setModalOpen(true);
  };

  const fetchGroups = async (testId) => {
    try {
      const res = await api.get(`/admin/tests/${testId}/subject-groups`, { headers: { Authorization: `Bearer ${token}` } });
      setSubjectGroups(res.data.groups);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load groups');
    }
  };

  const saveGroup = async () => {
    try {
      const payload = { name: groupModalData.name, subject_ids: groupModalData.subject_ids || [] };
      if (groupModalMode === 'add') {
        await api.post(`/admin/tests/${selectedTest.id}/subject-groups`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.put(`/admin/subject-groups/${groupModalData.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setGroupModalOpen(false);
      fetchGroups(selectedTest.id);
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  const deleteGroup = async (id) => {
    try {
      await api.delete(`/admin/subject-groups/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchGroups(selectedTest.id);
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  const itemList = () => {
    if (level === 'org') return orgs || [];
    if (level === 'type') return types || [];
    if (level === 'cycle') return cycles || [];
    if (level === 'test') return tests || [];
    return [];
  };

  const handleOpen = (item) => {
    if (level === 'org') {
      setSelectedOrg(item); setLevel('type'); fetchTypes(item.id);
    }
    if (level === 'type') {
      setSelectedType(item); setLevel('cycle'); fetchCycles(item.id);
    }
    if (level === 'cycle') {
      setSelectedCycle(item); setLevel('test'); fetchTests(item.id);
    }
  };

  const navigate = useNavigate();

  const closeGroups = () => {
    setShowGroupsManager(false);
    setSelectedTest(null);
  };

  const handleManageGroups = (item) => {
    navigate(`/exams/groups/${item.id}`, {
      state: {
        from: 'exams',
        orgId: selectedOrg?.id,
        typeId: selectedType?.id,
        cycleId: selectedCycle?.id,
        testId: item.id,
        test: item,
      },
    });
  };

  const back = () => {
    if (level === 'org') return null;
    return (
      <button className="mb-4 text-sm text-indigo-600 hover:underline" onClick={() => {
        if (level === 'type') setLevel('org');
        if (level === 'cycle') setLevel('type');
        if (level === 'test') setLevel('cycle');
      }}>
        Back
      </button>
    );
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-2xl font-bold text-indigo-700">Exam Management</h3>
          <p className="text-sm text-gray-600 mt-1">Enterprise-grade exam hierarchy for organizations, exam types, cycles, and tests.</p>
        </div>
        <div className="flex items-center gap-2">
          {back()}
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-lg shadow hover:bg-green-600 transition"
            onClick={() => openModal('add', {})}
          >
            + Add {level === 'org' ? 'Organization' : level === 'type' ? 'Exam Type' : level === 'cycle' ? 'Exam Cycle' : 'Test'}
          </button>
        </div>
      </div>

      <div className="mb-5 p-4 rounded-lg border border-indigo-100 bg-indigo-50">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
          {['org', 'type', 'cycle', 'test'].map((step) => (
            <span
              key={step}
              className={`px-2 py-1 rounded-full ${level === step ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'} `}
            >
              {step === 'org' ? 'Organization' : step === 'type' ? 'Exam Type' : step === 'cycle' ? 'Exam Cycle' : 'Test'}
            </span>
          ))}
        </div>
      </div>

      <ExamModal
        modalOpen={modalOpen}
        level={level}
        modalMode={modalMode}
        modalData={modalData}
        orgs={orgs}
        types={types}
        cycles={cycles}
        onClose={() => setModalOpen(false)}
        onChange={setModalData}
        onSave={saveItem}
      />
      <GroupManager
        show={showGroupsManager}
        selectedTest={selectedTest}
        subjectGroups={subjectGroups}
        subjects={subjects}
        onCloseGroups={closeGroups}
        onAddGroup={() => {
          setGroupModalMode('add');
          setGroupModalData({ name: '', subject_ids: [] });
          setGroupModalOpen(true);
        }}
        onEditGroup={(group) => {
          setGroupModalMode('edit');
          setGroupModalData({ id: group.id, name: group.name, subject_ids: group.subjects?.map((s) => s.id) || [] });
          setGroupModalOpen(true);
        }}
        onDeleteGroup={deleteGroup}
      />
      <ExamList
        items={itemList()}
        level={level}
        onEdit={(item) => { setModalMode('edit'); setModalData(item); setModalOpen(true); }}
        onDelete={deleteItem}
        onOpen={handleOpen}
        onManageGroups={handleManageGroups}
        onStatusChange={updateStatus}
      />
      <GroupModal
        open={groupModalOpen}
        mode={groupModalMode}
        data={groupModalData}
        subjects={subjects}
        onClose={() => setGroupModalOpen(false)}
        onChange={setGroupModalData}
        onSave={saveGroup}
      />
    </div>
  );
}
